import moment from 'moment';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

// TODO: Criar componente reusável para listagem e bloco de datas
// TODO: Criar testes unitários

const messages = defineMessages({
  published: {
    id: 'Published',
    defaultMessage: 'Published',
  },
  published_on: {
    id: 'Published on',
    defaultMessage: 'Published on',
  },
  created: {
    id: 'Created',
    defaultMessage: 'Created',
  },
  created_on: {
    id: 'Created on',
    defaultMessage: 'Created on',
  },
  modified: {
    id: 'Modified',
    defaultMessage: 'Modified',
  },
  modified_on: {
    id: 'Modified on',
    defaultMessage: 'Modified on',
  },
  not_published_yet: {
    id: 'Not published yet',
    defaultMessage: 'Not published yet',
  },
  not_saved_yet: {
    id: 'Not saved yet',
    defaultMessage: 'Not saved yet',
  },
});

const normalizeLocale = (locale) =>
  locale ? locale.toLowerCase().replace(/_/g, '-') : 'pt-br';

const resolveFirstDefinedValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

/**
 * Componente ContentDate
 *
 * Este componente exibe informações de datas relacionadas a um item, como data de criação,
 * publicação e modificação. Ele utiliza o formato relativo para exibir as datas e suporta
 * internacionalização via react-intl.
 *
 * Regras de exibição:
 * - Exibe a data de publicação (effective) caso exista.
 * - Caso contrário, exibe a data de criação (created).
 * - Se nenhuma das duas existir, exibe uma tag com o texto apropriado:
 *   - "Não salvo ainda" se a data de criação existir, mas não a de publicação.
 *   - "Não publicado ainda" se nenhuma data existir.
 * - Exibe a data de modificação (modified) caso ela exista e seja maior que a primeira data exibida.
 * - Caso especial: se review_state for null e apenas modified estiver presente, exibe modified como data de publicação.
 *
 * Propriedades:
 * - item: Objeto contendo as datas (created, modified, effective).
 * - className: Classe CSS adicional para estilização do componente.
 * - relative: Booleano para indicar se a data deve ser exibida em formato relativo (true) ou completo (false).
 * - hideModified: Booleano para indicar se a data de modificação deve ser ocultada.
 * - hideTag: Booleano para indicar se a tag deve ser ocultada.
 * - hideTime: Booleano para indicar se o horário deve ser ocultado, exibindo apenas a data.
 */
const ContentDate = ({
  item = {},
  className = '',
  relative = false,
  hideModified = false,
  hideTag = false,
  hideTime = false,
}) => {
  const intl = useIntl();
  const normalizedLocale = normalizeLocale(intl?.locale);

  const getMomentInstance = (date) => moment(date).locale(normalizedLocale);
  const effective = resolveFirstDefinedValue(
    item?.effective,
    item?.effective_date,
    item?.EffectiveDate,
  );
  const created = resolveFirstDefinedValue(item?.created, item?.CreationDate);
  const modified = resolveFirstDefinedValue(
    item?.modified,
    item?.ModificationDate,
  );
  const reviewState =
    item?.review_state !== undefined ? item.review_state : item?.reviewState;

  const {
    published,
    published_on,
    created: createdMsg,
    created_on,
    modified: modifiedMsg,
    modified_on,
    not_saved_yet,
    not_published_yet,
  } = messages;

  const formatDate = (date, isRelative) =>
    isRelative
      ? getMomentInstance(date).fromNow()
      : getMomentInstance(date).format(
          hideTime ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm',
        );

  const getContent = () => {
    if (!effective && !created && reviewState === null && modified) {
      // Caso especial para tipos sem workflow:
      // apenas modified está presente e review_state é null
      return `${intl.formatMessage(relative ? published : published_on)} ${formatDate(modified, relative)}`;
    }

    if (!effective && !created) return null;
    const message = effective
      ? relative
        ? published
        : published_on
      : relative
        ? createdMsg
        : created_on;
    return `${intl.formatMessage(message)} ${formatDate(effective || created, relative)}`;
  };

  const getModifiedContent = () => {
    const modifiedMoment = modified ? getMomentInstance(modified) : null;
    const displayMoment = displayDate ? getMomentInstance(displayDate) : null;

    if (
      hideModified ||
      !modifiedMoment ||
      (displayMoment && !modifiedMoment.isAfter(displayMoment))
    )
      return null;

    const isWithin24Hours =
      displayMoment && modifiedMoment.diff(displayMoment, 'hours') < 24;

    const formattedModifiedDate = isWithin24Hours
      ? modifiedMoment.fromNow()
      : modifiedMoment.format(hideTime ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm');

    return `${intl.formatMessage(isWithin24Hours ? modifiedMsg : modified_on)} ${formattedModifiedDate}`;
  };

  const getTagContent = () => {
    if (hideTag) return null;
    if (created && !effective)
      return <span>{intl.formatMessage(not_published_yet)}</span>;
    if (!created && !effective)
      return <span>{intl.formatMessage(not_saved_yet)}</span>;
    return null;
  };

  const displayDate =
    effective || created || (reviewState === null && modified);
  const content = getContent();
  const modifiedContent = getModifiedContent();
  const tagContent = getTagContent();

  return (
    <span
      className={`d-inline-flex flex-column flex-sm-row align-items-center text-gray-70 ${className}`}
    >
      <span>{content}</span>
      {modifiedContent && (
        <>
          <span className="br-divider vertical d-none d-sm-block mx-baseh" />
          <span>{modifiedContent}</span>
        </>
      )}
      {tagContent && (
        <span className="br-tag bg-gray-10 text-gray-80 m-2 m-base my-md-0 mx-md-3">
          {tagContent}
        </span>
      )}
    </span>
  );
};

ContentDate.propTypes = {
  item: PropTypes.shape({
    created: PropTypes.string, // Data de criação do item
    CreationDate: PropTypes.string,
    modified: PropTypes.string, // Data de modificação do item
    ModificationDate: PropTypes.string,
    effective: PropTypes.string, // Data de publicação do item
    effective_date: PropTypes.string,
    EffectiveDate: PropTypes.string,
    review_state: PropTypes.string, // Estado de revisão do item
    reviewState: PropTypes.string,
  }).isRequired,
  className: PropTypes.string, // Classe CSS adicional
  relative: PropTypes.bool, // Indica se a data deve ser exibida em formato relativo ou completo
  hideModified: PropTypes.bool, // Indica se a data de modificação deve ser ocultada
  hideTag: PropTypes.bool, // Indica se a tag deve ser ocultada
  hideTime: PropTypes.bool, // Indica se o horário deve ser ocultado
};

export default ContentDate;
